import { Button, EmptyState, Field, Spinner } from '@freightapi/ui'
import { createFileRoute } from '@tanstack/react-router'
import { type FormEvent, useCallback, useEffect, useState } from 'react'
import { SecretPanel } from '../components/SecretPanel'
import {
  type ApiError,
  asApiError,
  createWebhookEndpoint,
  deleteWebhookEndpoint,
  fetchWebhookEndpoints,
  testWebhookEndpoint,
  updateWebhookEndpoint,
  type WebhookEndpoint,
} from '../lib/api'
import { useConsoleI18n } from '../lib/locale'
import { useMode } from '../lib/mode'
import { useCan } from '../lib/session'

/**
 * Webhook endpoints (M3-03), on the console alias of the M1-13 routes. Per mode, like keys: a
 * receiver that is subscribed in the sandbox is not subscribed in production.
 *
 * "Send test" is deliberately synchronous. A developer who just pasted a URL wants to know whether
 * it answers, and a queued delivery that shows up in a log somewhere later does not answer that
 * question — so the button reports the status code and the duration of one real attempt.
 */
export const Route = createFileRoute('/_app/webhooks/')({ component: Webhooks })

interface TestOutcome {
  readonly endpointId: string
  readonly message: string
  readonly failed: boolean
}

function Webhooks() {
  const { t } = useConsoleI18n()
  const can = useCan()
  const { mode } = useMode()
  const [state, setState] = useState<
    | { status: 'loading' }
    | { status: 'ready'; endpoints: readonly WebhookEndpoint[] }
    | { status: 'error'; error: ApiError }
  >({ status: 'loading' })
  const [secret, setSecret] = useState<string | undefined>(undefined)
  const [error, setError] = useState<string | undefined>(undefined)
  const [outcome, setOutcome] = useState<TestOutcome | undefined>(undefined)
  const [formOpen, setFormOpen] = useState(false)
  const [testing, setTesting] = useState<string | undefined>(undefined)

  const reload = useCallback(async () => {
    try {
      setState({ status: 'ready', endpoints: (await fetchWebhookEndpoints(mode)).endpoints })
    } catch (cause) {
      setState({ status: 'error', error: asApiError(cause) })
    }
  }, [mode])

  useEffect(() => {
    void reload()
  }, [reload])

  const run = useCallback(
    async (action: () => Promise<unknown>) => {
      setError(undefined)
      try {
        await action()
        await reload()
        return true
      } catch (cause) {
        const failure = asApiError(cause)
        setError(failure.problem?.detail ?? t(failure.messageKey))
        return false
      }
    },
    [reload, t],
  )

  const sendTest = useCallback(
    async (endpoint: WebhookEndpoint) => {
      setError(undefined)
      setTesting(endpoint.id)
      try {
        const { result } = await testWebhookEndpoint(endpoint.id, mode)
        setOutcome({
          endpointId: endpoint.id,
          failed: result.status !== 'succeeded',
          message:
            result.response_status === null
              ? t('webhooks.test.failed', {
                  duration: String(result.duration_ms),
                  error: result.error ?? '',
                })
              : t('webhooks.test.result', {
                  status: String(result.response_status),
                  duration: String(result.duration_ms),
                }),
        })
      } catch (cause) {
        const failure = asApiError(cause)
        setError(failure.problem?.detail ?? t(failure.messageKey))
      } finally {
        setTesting(undefined)
      }
    },
    [mode, t],
  )

  const write = can('webhooks:write')

  return (
    <section className="flex flex-col gap-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-semibold text-lead text-ink">{t('webhooks.nav.endpoints')}</h2>
        {write ? (
          <Button onClick={() => setFormOpen((open) => !open)}>{t('webhooks.new')}</Button>
        ) : null}
      </div>
      <p className="text-body text-muted">{t('webhooks.intro')}</p>

      {error === undefined ? null : (
        <p role="alert" className="text-body text-danger">
          {error}
        </p>
      )}

      {secret === undefined ? null : (
        <SecretPanel
          titleKey="webhooks.secret.title"
          noteKey="webhooks.secret.once"
          secret={secret}
          dismissKey="api_keys.created.done"
          onDismiss={() => setSecret(undefined)}
        />
      )}

      {write && formOpen ? (
        <EndpointForm
          onCreate={async (input) => {
            setError(undefined)
            try {
              const { endpoint } = await createWebhookEndpoint(mode, input)
              setSecret(endpoint.secret)
              setFormOpen(false)
              await reload()
              return true
            } catch (cause) {
              const failure = asApiError(cause)
              setError(failure.problem?.detail ?? t(failure.messageKey))
              return false
            }
          }}
          onCancel={() => setFormOpen(false)}
        />
      ) : null}

      {state.status === 'loading' ? (
        <div className="flex justify-center py-10">
          <Spinner label={t('loading')} size="lg" />
        </div>
      ) : state.status === 'error' ? (
        <EmptyState title={t('error.title')} description={t(state.error.messageKey)} />
      ) : state.endpoints.length === 0 ? (
        <EmptyState title={t('webhooks.empty')} description={t('webhooks.intro')} />
      ) : (
        <ul className="flex list-none flex-col gap-3 p-0">
          {state.endpoints.map((endpoint) => (
            <li
              key={endpoint.id}
              className="flex flex-col gap-2 rounded-card border border-line px-4 py-3"
            >
              <div className="flex flex-wrap items-center justify-between gap-3">
                <span className="flex min-w-0 flex-col">
                  <span className="break-all font-medium text-body text-ink">{endpoint.url}</span>
                  <span className="text-caption text-muted">
                    {endpoint.events.join(', ')} ·{' '}
                    {t(endpoint.active ? 'webhooks.status.active' : 'webhooks.status.inactive')}
                    {endpoint.description === null ? '' : ` · ${endpoint.description}`}
                  </span>
                </span>
                {write ? (
                  <span className="flex gap-2">
                    <Button
                      variant="ghost"
                      busy={testing === endpoint.id}
                      busyLabel={t('loading')}
                      onClick={() => void sendTest(endpoint)}
                    >
                      {t('webhooks.test')}
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() =>
                        void run(() =>
                          updateWebhookEndpoint(endpoint.id, mode, { active: !endpoint.active }),
                        )
                      }
                    >
                      {t(endpoint.active ? 'webhooks.pause' : 'webhooks.activate')}
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => {
                        if (!window.confirm(t('webhooks.delete.confirm', { url: endpoint.url })))
                          return
                        void run(() => deleteWebhookEndpoint(endpoint.id, mode))
                      }}
                    >
                      {t('webhooks.delete')}
                    </Button>
                  </span>
                ) : null}
              </div>
              {outcome?.endpointId === endpoint.id ? (
                <p
                  role="status"
                  className={outcome.failed ? 'text-body text-danger' : 'text-body text-muted'}
                >
                  {outcome.message}
                </p>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

function EndpointForm({
  onCreate,
  onCancel,
}: {
  readonly onCreate: (input: {
    url: string
    events: readonly string[]
    description?: string
  }) => Promise<boolean>
  readonly onCancel: () => void
}) {
  const { t } = useConsoleI18n()
  const [url, setUrl] = useState('')
  const [events, setEvents] = useState('*')
  const [description, setDescription] = useState('')
  const [busy, setBusy] = useState(false)

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setBusy(true)
    const trimmed = description.trim()
    const created = await onCreate({
      url: url.trim(),
      events: events
        .split(',')
        .map((entry) => entry.trim())
        .filter((entry) => entry !== ''),
      ...(trimmed === '' ? {} : { description: trimmed }),
    })
    setBusy(false)
    if (created) {
      setUrl('')
      setDescription('')
    }
  }

  return (
    <form
      className="flex flex-col gap-4 rounded-card border border-line p-4"
      onSubmit={(event) => void onSubmit(event)}
    >
      <h2 className="font-semibold text-lead text-ink">{t('webhooks.form.title')}</h2>
      <Field label={t('webhooks.form.url')} required>
        {({ id, describedBy, invalid }) => (
          <input
            id={id}
            aria-describedby={describedBy}
            aria-invalid={invalid || undefined}
            type="url"
            required
            value={url}
            onChange={(event) => setUrl(event.target.value)}
            className="min-h-touch w-full rounded-card border border-line bg-surface px-3 text-body text-ink"
          />
        )}
      </Field>
      <Field label={t('webhooks.form.events')} hint={t('webhooks.form.events.hint')} required>
        {({ id, describedBy }) => (
          <input
            id={id}
            aria-describedby={describedBy}
            type="text"
            required
            value={events}
            onChange={(event) => setEvents(event.target.value)}
            className="min-h-touch w-full rounded-card border border-line bg-surface px-3 text-body text-ink"
          />
        )}
      </Field>
      <Field label={t('webhooks.form.description')}>
        {({ id, describedBy }) => (
          <input
            id={id}
            aria-describedby={describedBy}
            type="text"
            maxLength={200}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            className="min-h-touch w-full rounded-card border border-line bg-surface px-3 text-body text-ink"
          />
        )}
      </Field>
      <div className="flex flex-wrap gap-2">
        <Button type="submit" busy={busy} busyLabel={t('loading')}>
          {t('webhooks.form.submit')}
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel}>
          {t('button.cancel')}
        </Button>
      </div>
    </form>
  )
}
